import { ClickAwayListener } from "@mui/base";
import { useOpenState } from "@sinch/hooks/utilities/useOpenState";
import { MdsKeyboardArrowDown, MdsKeyboardArrowUp } from "@sinch/icons";
import type { ConditionCategory } from "@sinch/types/sinch.types";
import React, { FocusEventHandler, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdsIcon } from "@sinch/components";
import {
  ConditionInputValue,
  ConditionSelectProps,
} from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/ConditionInput";
import {
  ConditionSubjectInputAdornment,
  ConditionSubjectInputBase,
  ConditionSubjectPaper,
  ConditionSubjectPopper,
  ContainerBox,
} from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/ConditionSubjectSelect/components";
import { ConditionSubjectLabel } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/ConditionSubjectSelect/ConditionSubjectLabel";
import { useSubjectSelect } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/ConditionSubjectSelect/hooks";
import { searchLabelPath } from "@sinch/components/form/MultilevelSelect/hooks/optionTreeTools";
import { MultilevelSelect } from "@sinch/components/form/MultilevelSelect/MultilevelSelect";

export type ConditionSubjectValue = Pick<ConditionInputValue, "subjectId" | "type"> & {
  category: ConditionCategory | null;
};
export type ConditionSubjectSelectProps = ConditionSelectProps<ConditionSubjectValue> & {
  //options: NestedOptions<ConditionSubjectValue | null>[];
  empty?: boolean;
  onBlur?: () => void;
};
export const ConditionSubjectSelect = ({
  value,
  onChange,
  onOpenChange,
  empty,
  onBlur,
  readOnly,
}: ConditionSubjectSelectProps) => {
  const { t } = useTranslation();
  const [opened, open, close, toggle] = useOpenState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLDivElement>(null);

  const { options } = useSubjectSelect(value);

  const handleBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = useCallback(
    (e) => {
      if (!opened) {
        onBlur?.(e);
      }
    },
    [onBlur, opened]
  );

  const getLabelPath = (val: ConditionSubjectValue | null): string[] => {
    if (val === null) {
      return [t("RateBuilder.noConditionOption")];
    }
    return searchLabelPath(options, val) ?? [];
  };

  useEffect(() => {
    onOpenChange(opened);
  }, [onOpenChange, opened]);

  const handleInputAdornmentClick = useCallback(() => {
    if (readOnly) {
      return;
    }
    toggle();
    if (opened) {
      inputRef.current?.focus();
    }
  }, [readOnly, toggle]);

  const handleInputClick = useCallback(() => {
    if (readOnly) {
      return;
    }
    open();
  }, [open, readOnly]);

  const handleClickAway = useCallback(() => {
    if (opened) {
      close();
      inputRef.current?.focus();
    }
  }, [close, opened]);

  const handleMultilevelSelectChange = useCallback<(_: any, value: ConditionSubjectValue | null) => void>(
    (e, val) => {
      onChange?.(val);
      open();
      handleBlur?.(e);
    },
    [onChange, open, handleBlur]
  );

  return (
    <ContainerBox>
      <div ref={setAnchorEl}>
        <ConditionSubjectInputBase
          endAdornment={
            <ConditionSubjectInputAdornment onClick={handleInputAdornmentClick} position="end">
              {opened ? (
                <MdsIcon fontSize="small" icon={MdsKeyboardArrowUp} />
              ) : (
                <MdsIcon fontSize="small" icon={MdsKeyboardArrowDown} />
              )}
            </ConditionSubjectInputAdornment>
          }
          fullWidth
          inputComponent={ConditionSubjectLabel}
          inputProps={{
            ref: inputRef,
            "aria-expanded": opened,
            "data-cy": "ConditionInput-subjectSelect",
            labelPath: empty ? [] : getLabelPath(value),
            placeholder: t("RateBuilder.selectCondition"),
            onBlur: handleBlur,
            onClick: handleInputClick,
          }}
          readOnly
        />
      </div>
      <ConditionSubjectPopper anchorEl={anchorEl} disablePortal open={opened}>
        <ClickAwayListener onClickAway={handleClickAway}>
          <ConditionSubjectPaper>
            <MultilevelSelect
              data-cy="ConditionInput-subjectSelect-dropdown"
              onChange={handleMultilevelSelectChange}
              options={options}
              value={value ?? null}
            />
          </ConditionSubjectPaper>
        </ClickAwayListener>
      </ConditionSubjectPopper>
    </ContainerBox>
  );
};
